'use client';

import { useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { isValidRoomName } from '@/lib/rooms/client-utils';

import SearchIcon from '@/assets/icons/search.svg';

import elements from '@/styles/elements.module.sass';
import styles from './RoomInput.module.sass';

const formatRoomName = (raw: string) => {
  const letters = raw
    .replace(/[^A-Za-z]/g, '')
    .toUpperCase()
    .slice(0, 9);
  if (letters.length <= 3) return letters.length === 3 ? `${letters}-` : letters;
  if (letters.length <= 6)
    return letters.length === 6
      ? `${letters.slice(0, 3)}-${letters.slice(3)}-`
      : `${letters.slice(0, 3)}-${letters.slice(3)}`;
  return `${letters.slice(0, 3)}-${letters.slice(3, 6)}-${letters.slice(6)}`;
};

const RU_TO_EN_KEYMAP: Record<string, string> = {
  й: 'q',
  Й: 'q',
  ц: 'w',
  Ц: 'w',
  у: 'e',
  У: 'e',
  к: 'r',
  К: 'r',
  е: 't',
  Е: 't',
  н: 'y',
  Н: 'y',
  г: 'u',
  Г: 'u',
  ш: 'i',
  Ш: 'i',
  щ: 'o',
  Щ: 'o',
  з: 'p',
  З: 'p',
  ф: 'a',
  Ф: 'a',
  ы: 's',
  Ы: 's',
  в: 'd',
  В: 'd',
  а: 'f',
  А: 'f',
  п: 'g',
  П: 'g',
  р: 'h',
  Р: 'h',
  о: 'j',
  О: 'j',
  л: 'k',
  Л: 'k',
  д: 'l',
  Д: 'l',
  я: 'z',
  Я: 'z',
  ч: 'x',
  Ч: 'x',
  с: 'c',
  С: 'c',
  м: 'v',
  М: 'v',
  и: 'b',
  И: 'b',
  т: 'n',
  Т: 'n',
  ь: 'm',
  Ь: 'm',
};

const isDesktop = () => {
  if (typeof navigator === 'undefined') return true;
  return !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const mapRuToEn = (value: string) =>
  value.replace(/[ЁёА-Яа-я]/g, (ch) => RU_TO_EN_KEYMAP[ch] ?? ch);

type Props = {
  onJoinAction: (roomName: string) => void;
  className?: string;
  placeholderLabel?: string;
};

export default function RoomInput({ onJoinAction, className, placeholderLabel }: Props) {
  const [roomName, setRoomName] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useHotkeys('j', (e) => {
    e.preventDefault();
    inputRef.current?.focus();
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const normalized = isDesktop() ? mapRuToEn(raw) : raw;
    setRoomName(formatRoomName(normalized));
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onJoinAction(roomName);
      return;
    }
    if (e.key !== 'Backspace' || !roomName) return;
    const input = e.target as HTMLInputElement;
    const pos = input.selectionStart ?? 0;
    if (roomName[pos - 1] === '-') {
      e.preventDefault();
      const without = roomName.slice(0, pos - 2) + roomName.slice(pos - 1);
      setRoomName(formatRoomName(without));
      setTimeout(() => input.setSelectionRange(pos - 2, pos - 2), 0);
    }
  };
  const valid = isValidRoomName(roomName);
  return (
    <div className={className}>
      <div className={styles.inputWrapper}>
        <span className={styles.label}>{placeholderLabel ?? 'Enter existing room name'}</span>
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          placeholder={focused ? '———-———-———' : 'QWE-LBV-NEO'}
          value={roomName}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        <kbd
          className={`${elements.key} ${styles.shortcut}`}
          data-dim={(focused && !valid) || undefined}
          onMouseDown={(e) => {
            e.preventDefault();
            if (focused && valid) {
              onJoinAction(roomName);
            } else {
              inputRef.current?.focus();
            }
          }}
        >
          {focused ? (
            'ENTER'
          ) : (
            <>
              <SearchIcon width={13} height={13} />J
            </>
          )}
        </kbd>
      </div>
    </div>
  );
}
